import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { unparse } from 'papaparse';

import { apiAdminusersGetCollection } from '@r/api/generated/client.ts';
import type {
  ApiAdminusersGetCollectionMaxRoleItem,
  ApiAdminusersGetCollectionParams,
  ApiAdminusersGetCollectionStatusItem,
  UserUserCollectionReadModel,
  UserWorkspaceUserCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useFilterFields, useUserColumns } from '@r/components/home/user-columns.tsx';

import { downloadCSV } from '@r/utils/utils.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type UserCollection = UserUserCollectionReadModel;
type StatusType = UserWorkspaceUserCollectionReadModel['status'];

export default function UserList() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);
  const user = useUser();
  const { sanitizedWorkspaceName } = useWorkspaceName();

  async function usersListFiltersQuery(
    filters: {
      firstname: string;
      lastname: string;
      email: string;
      company: string;
      firstnameSortValue: 'asc' | 'desc' | undefined;
      lastnameSortValue: 'asc' | 'desc' | undefined;
      commercialSortValue: 'asc' | 'desc' | undefined;
      registeredAtSortValue: 'asc' | 'desc' | undefined;
      createdAtSortValue: 'asc' | 'desc' | undefined;
      status: ApiAdminusersGetCollectionStatusItem[] | undefined;
      phone: string;
      maxRole: ApiAdminusersGetCollectionMaxRoleItem[] | undefined;
      responsible_fullname: string;
      uniqueCode: string;
      comment: string;
      comment2: string;
      comment3: string;
      registeredAtAfter: string;
      registeredAtBefore: string;
      createdAtAfter: string;
      createdAtBefore: string;
    },
    page: number | null = null,
    limit: number | null = null
  ) {
    const {
      firstname,
      lastname,
      email,
      company,
      firstnameSortValue,
      lastnameSortValue,
      commercialSortValue,
      registeredAtSortValue,
      createdAtSortValue,
      status,
      phone,
      maxRole,
      responsible_fullname,
      uniqueCode,
      comment,
      comment2,
      comment3,
      registeredAtAfter,
      registeredAtBefore,
      createdAtAfter,
      createdAtBefore
    } = filters;

    try {
      const params: ApiAdminusersGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(firstname ? { firstname } : {}),
        ...(lastname ? { lastname } : {}),
        ...(email ? { email } : {}),
        ...(company ? { 'userWorkspaces.company.name': company } : {}),
        ...(status?.length ? { 'status[]': status } : {}),
        ...(phone ? { phone } : {}),
        ...(maxRole?.length ? { 'maxRole[]': maxRole } : {}),
        ...(responsible_fullname ? { responsible_fullname } : {}),
        ...(uniqueCode ? { 'userWorkspaces.uniqueCode': uniqueCode } : {}),
        ...(comment ? { 'userWorkspaces.comment': comment } : {}),
        ...(comment2 ? { 'userWorkspaces.comment2': comment2 } : {}),
        ...(comment3 ? { 'userWorkspaces.comment3': comment3 } : {}),
        ...(registeredAtAfter ? { 'userWorkspaces.registeredAt[after]': registeredAtAfter } : {}),
        ...(registeredAtBefore
          ? { 'userWorkspaces.registeredAt[before]': registeredAtBefore }
          : {}),
        ...(createdAtAfter ? { 'userWorkspaces.createdAt[after]': createdAtAfter } : {}),
        ...(createdAtBefore ? { 'userWorkspaces.createdAt[before]': createdAtBefore } : {}),
        ...(firstnameSortValue ? { 'order[firstname]': firstnameSortValue } : {}),
        ...(lastnameSortValue ? { 'order[lastname]': lastnameSortValue } : {}),
        ...(commercialSortValue ? { 'order[commercial]': commercialSortValue } : {}),
        ...(registeredAtSortValue
          ? { 'order[userWorkspaces.registeredAt]': registeredAtSortValue }
          : {}),
        ...(createdAtSortValue ? { 'order[userWorkspaces.createdAt]': createdAtSortValue } : {})
      };

      return await apiAdminusersGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/users')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  function usersListFilters() {
    const firstname =
      (columnFilters.find((filter) => filter.id === 'firstname')?.value as string) ?? '';

    const lastname =
      (columnFilters.find((filter) => filter.id === 'lastname')?.value as string) ?? '';

    const email = (columnFilters.find((filter) => filter.id === 'email')?.value as string) ?? '';

    const company =
      (columnFilters.find((filter) => filter.id === 'company')?.value as string) ?? '';

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    const statusFilter = columnFilters.find((filter) => filter.id === 'status');
    const status = (statusFilter?.value as StatusType[] | undefined)?.filter(
      (value): value is ApiAdminusersGetCollectionStatusItem => value != null
    );

    const phone = (columnFilters.find((filter) => filter.id === 'phone')?.value as string) ?? '';
    const maxRoleFilter = columnFilters.find((filter) => filter.id === 'maxRole');
    const maxRole = maxRoleFilter?.value as ApiAdminusersGetCollectionMaxRoleItem[] | undefined;
    const responsible_fullname =
      (columnFilters.find((filter) => filter.id === 'commercial')?.value as string) ?? '';
    const uniqueCode =
      (columnFilters.find((filter) => filter.id === 'uniqueCode')?.value as string) ?? '';
    const comment =
      (columnFilters.find((filter) => filter.id === 'comment')?.value as string) ?? '';
    const comment2 =
      (columnFilters.find((filter) => filter.id === 'comment2')?.value as string) ?? '';
    const comment3 =
      (columnFilters.find((filter) => filter.id === 'comment3')?.value as string) ?? '';
    const registeredAtAfter =
      (columnFilters.find((filter) => filter.id === 'registeredAtAfter')?.value as string) ?? '';
    const registeredAtBefore =
      (columnFilters.find((filter) => filter.id === 'registeredAtBefore')?.value as string) ?? '';
    const createdAtAfter =
      (columnFilters.find((filter) => filter.id === 'createdAtAfter')?.value as string) ?? '';
    const createdAtBefore =
      (columnFilters.find((filter) => filter.id === 'createdAtBefore')?.value as string) ?? '';

    return {
      firstname,
      lastname,
      email,
      company,
      firstnameSortValue: sortValue('firstname'),
      lastnameSortValue: sortValue('lastname'),
      commercialSortValue: sortValue('commercial'),
      registeredAtSortValue: sortValue('registeredAt'),
      createdAtSortValue: sortValue('createdAt'),
      status,
      phone,
      maxRole,
      responsible_fullname,
      uniqueCode,
      comment,
      comment2,
      comment3,
      registeredAtAfter,
      registeredAtBefore,
      createdAtAfter,
      createdAtBefore
    };
  }

  function getUserDataForExport(user: UserCollection) {
    const sanitize = (value: string | undefined) => value?.replace(/[\r\n\t]/g, '') ?? '';

    const userFirstname = user.firstname;
    const userLastname = user.lastname;
    const userEmail = user.email;

    const activeWorkspace = getActiveWorkspaceUniversal(user.userWorkspaces);
    const userPhone = activeWorkspace?.phone ?? '';

    let userStatus = '';

    if (activeWorkspace?.status) {
      userStatus = t(activeWorkspace.status);
    }

    const userRole = activeWorkspace?.maxRole
      ? t(`${activeWorkspace.maxRole.toLowerCase().replaceAll('_', '.')}`)
      : '';

    const userCompany = activeWorkspace?.company?.name ?? '';

    const userCompanyResponsible = activeWorkspace?.company?.responsiblesFullName?.join(', ') ?? '';

    const userUniqueCode = activeWorkspace?.uniqueCode ?? '';
    const userComment = activeWorkspace?.comment ?? '';
    const userComment2 = activeWorkspace?.comment2 ?? '';
    const userComment3 = activeWorkspace?.comment3 ?? '';
    const userCreatedAt = activeWorkspace?.createdAt
      ? format(activeWorkspace.createdAt, 'yyyy-MM-dd')
      : '';
    const userRegisteredAt = activeWorkspace?.registeredAt
      ? format(new Date(activeWorkspace.registeredAt as string), 'yyyy-MM-dd')
      : '';

    return [
      userStatus,
      sanitize(userFirstname),
      sanitize(userLastname),
      sanitize(userEmail),
      sanitize(userPhone),
      userRole,
      sanitize(userCompany),
      userCompanyResponsible,
      userUniqueCode,
      sanitize(userComment),
      sanitize(userComment2),
      sanitize(userComment3),
      userCreatedAt,
      userRegisteredAt
    ];
  }

  const usersListQuery = useQuery({
    queryKey: ['users_list'],
    queryFn: async () => {
      const users = await usersListFiltersQuery(usersListFilters());

      for (let i = 0; i < users.data.length; i++) {
        (users.data[i] as UserCollection & { url: string }).url = `/users/${users.data[i].id}`;
      }

      return users;
    }
  });

  const filterFields = useFilterFields();

  const usersTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver les preferences de table.
    () => getAdminTablePersistenceKey('users', user.data?.id),
    [user.data?.id]
  );

  const { table } = useDataTable({
    data: usersListQuery.data?.data ?? [],
    columns: useUserColumns(),
    filterFields,
    pageCount: usersListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: usersTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    const limit = 500;

    const totalPageCount = Math.ceil((usersListQuery.data?.pagination?.totalItems ?? 1) / limit);

    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      usersListFiltersQuery(usersListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);

    const allData: UserCollection[] = pagesData.flatMap((pageData) => pageData.data);

    const fields = [
      t('label.user.status'),
      t('label.user.firstname'),
      t('label.user.lastname'),
      t('label.user.email'),
      t('label.user.phone'),
      t('label.user.role'),
      t('label.user.company'),
      t('label.company.responsible'),
      t('label.user.unique.code'),
      t('label.user.comment'),
      t('label.user.comment2'),
      t('label.user.comment3'),
      t('label.user.created.at'),
      t('label.user.registered.at')
    ];

    const usersData = allData.map((user) => getUserDataForExport(user));

    const csv = unparse({
      fields: fields,
      data: usersData
    });

    const currentDate = new Date();

    const baseFileName = `users-export-${format(currentDate, 'yyyy-MM-dd-HHmmss')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;

    downloadCSV(csv, fileName);
    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      usersListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.users.title')}</span>

      {usersListQuery.isLoading && (
        <DataTableSkeleton
          columnCount={15}
          cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
        />
      )}

      {usersListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />
          <DataTable table={table} totalItems={usersListQuery.data?.pagination?.totalItems} />
        </>
      )}
    </>
  );
}
